import { useEffect, useState } from 'react';
import { createHtmlField, createRadioGroupFieldWithParameters, createSelectField, createTextField, NumericAutocompleteField } from '@roc/ui';
import { observer } from 'mobx-react';
import { useMemo, useRef } from 'react';
import { Box, Typography, TextField, InputLabel } from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';
import { Theme, createStyles, makeStyles } from '@material-ui/core/styles';
import { Grid } from '@material-ui/core';
import { TaskPriorityField } from '../../editTask/taskPriorityField';
import { generalTaskPriorityOptions, defaultSectionName, LOAN_RELATED_DOCUMENTS, LOAN_RELATED_DOCUMENTS_ID } from '../../../utils/generalTasksConstants';
import { useSearchBarStore, useUserStore } from '@roc/feature-app-core';
import { Search } from '@material-ui/icons';
import { debounce } from '@roc/feature-utils';
import { GeneralTasksStore } from '../../../stores/generalTasksStore';
import { TaskScopeForm } from '../../../types/generalTasksTypes';
import { TaskAssigneeField } from '../../editTask/taskAssigneeField';
import { RadioGroup, RadioGroupOption } from './radioGroup';
import { TaskWatchField } from '../../editTask/taskWatchField';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    container: {
      width: '100%',
    },
    badge: {
      marginRight: '5px',
    },
    inputLabel: {
      marginBottom: theme.spacing(1),
      color: '#222222',
    }
  }),
);

type LoanTaskFormProps = {
  generalTasksStore: GeneralTasksStore;
  options: any[];
  isSectionPreselected: boolean;
};

export const LoanTaskForm = observer(({
  generalTasksStore,
  options,
  isSectionPreselected,
}: LoanTaskFormProps) => {
  const inputRef = useRef<HTMLInputElement>();
  const { editTaskStore, globalStore, assigneeOptions, watcherOptions } = generalTasksStore;
  const { userStore } = useUserStore();

  const isInternalUser = useMemo(() => { return userStore.userInformation?.isInternal; }, [userStore.userInformation]);

  const isInternal = globalStore.isInternalPortal || (generalTasksStore?.globalStore?.isLoanChatApp && isInternalUser) ;
  const { loanTaskFormStore } = editTaskStore;
  const { searchBarStore } = useSearchBarStore();
  useEffect(() => {
    searchBarStore.reset();
  }, []);
  const fetchOptions = useMemo(
    () => debounce(searchText => editTaskStore.fetchOptions(searchText), 500),
    [editTaskStore]
  );
  const onSelect = (val) => {
    if (!isInternal) {
      generalTasksStore.fetchLoanActorsAssigneeOptions(Number(val?.loanId));
    }
    editTaskStore.fetchLoanTaskSectionNames(Number(val?.loanId), 'Underwriting');
    loanTaskFormStore.onFieldChange("loanId", val?.loanId);
  }

  useEffect(() => {
    if (loanTaskFormStore.form.fields.loanId.value && isInternal) {
      editTaskStore.fetchLoanTaskSectionNames(Number(loanTaskFormStore.form.fields.loanId.value), 'Underwriting');
    }
  }, []);

  useEffect(() => {
    if (!isInternalUser) {
      loanTaskFormStore.onFieldChange('sectionId', LOAN_RELATED_DOCUMENTS_ID);
      loanTaskFormStore.onFieldChange('sectionName', LOAN_RELATED_DOCUMENTS);
    }
  }, [isInternalUser, loanTaskFormStore]);

  const isLoanView = !!generalTasksStore.loanId;

  const classes = useStyles();

  const taskScopeRadioGroupOptions: RadioGroupOption[] = [
    {
      value: TaskScopeForm.INTERNAL,
      label: TaskScopeForm.INTERNAL,
      onClick: () => { loanTaskFormStore.onFieldChange('taskScope', TaskScopeForm.INTERNAL) },
      checked: loanTaskFormStore.form.fields.taskScope.value === TaskScopeForm.INTERNAL,
    },
    {
      value: TaskScopeForm.EXTERNAL,
      label: TaskScopeForm.EXTERNAL,
      onClick: () => { loanTaskFormStore.onFieldChange('taskScope', TaskScopeForm.EXTERNAL) },
      checked: loanTaskFormStore.form.fields.taskScope.value === TaskScopeForm.EXTERNAL,
    }
  ];

  const getAssigneeOptions = () => {
    const taskScope = loanTaskFormStore.form.fields.taskScope.value;

    if (taskScope === TaskScopeForm.INTERNAL) {
      return assigneeOptions.filter(assignee => assignee.isRoleInternal);
    }
    return assigneeOptions;
  }

  const getWatchersOptions = () => {
    const taskScope = loanTaskFormStore.form.fields.taskScope.value;

    if (taskScope === TaskScopeForm.INTERNAL) {
      return watcherOptions.filter(assignee => assignee.isRoleInternal);
    }
    return watcherOptions;
  }

  const SearchOption = ({ option }) => {
    return (
      <Box overflow={'hidden'} width={'100%'} p={1}>
        <Box flex={1}>
          <Typography>
            <b>{option.loanId}</b> - {option.dealName}
          </Typography>
        </Box>
        <Typography>{option.lender}</Typography>
      </Box>
    );
  };
  const search = useMemo(
    () =>
      debounce(value => {
        if (value.length >= 3) {
          searchBarStore.searchLoans(value);
        } else {
          searchBarStore.reset();
        }
      }, 500),
    []
  );

  const [inputValue, setInputValue] = useState("");

  return (
    <>
      <Grid container spacing={2} innerRef={inputRef}>
        {!isLoanView && <Grid item xs={12}>
          <InputLabel
            htmlFor="menu-appbar"
            className={classes.inputLabel}
          >
            Search Loan
          </InputLabel>
          <Autocomplete
            fullWidth
            id="menu-appbar"
            options={searchBarStore.searchResults}
            componentName="menu-appbar"
            inputValue={inputValue}
            renderInput={options => (
              <TextField
                {...options}
                autoFocus
                fullWidth
                variant="outlined"
                InputLabelProps={{ shrink: true }}
                InputProps={{
                  ...options.InputProps,
                  startAdornment:
                    inputValue.length === 0 ? <Search /> : <></>,
                }}
              />
            )}
            renderOption={option => <SearchOption option={option} />}
            blurOnSelect={true}
            getOptionLabel={(option: any) => option.loanId.toString()}
            loading={searchBarStore.loading}
            filterOptions={options => options}
            autoSelect={false}
            onChange={(e, value) => {
              onSelect(value);
            }}
            onInputChange={(e, value) => {
              setInputValue(value);
              search(value);
            }}
          />
        </Grid>}
        {
          isLoanView && <Grid item xs={12}>
            <NumericAutocompleteField
              label="Loan Id"
              placeHolder="Select a Loan Id"
              value={loanTaskFormStore.form.fields.loanId.value}
              options={editTaskStore.loanSearchOptions}
              onChange={text => {
                fetchOptions(text);
              }}
              onSelect={opt => {
                if (!isInternal) {
                  generalTasksStore.fetchLoanActorsAssigneeOptions(Number(opt.value));
                }
                editTaskStore.fetchLoanTaskSectionNames(Number(opt.value), 'Underwriting');
                loanTaskFormStore.onFieldChange("loanId", opt.value);
              }}
              disabled={isLoanView}
            />
          </Grid>
        }
        {isInternal && (
          <Grid item xs={12}>
            <RadioGroup
              label='Please select one: *'
              options={taskScopeRadioGroupOptions}
            />
          </Grid>
        )}
        <Grid item xs={12}>
          <TaskAssigneeField
            label={'Assignee'}
            required
            value={loanTaskFormStore.form.fields.assigneeId.value}
            onChange={(e, value) => loanTaskFormStore.onFieldChange('assigneeId', value)}
            assigneeOptions={getAssigneeOptions()}
            disabled={getAssigneeOptions()?.length === 0}
            error={loanTaskFormStore.form.fields.assigneeId.error}
          />
        </Grid>
        <Grid item xs={12}>
          <TaskWatchField
            label={'Watchers'}
            value={loanTaskFormStore.form.fields.watchIds.value || []}
            onChange={(event, value) => loanTaskFormStore.onFieldChange('watchIds', value)}
            assigneeOptions={getWatchersOptions()}
            disabled={getWatchersOptions()?.length === 0}
          />
        </Grid>
        {isInternal && (
          <Grid item xs={12}>
            <TaskPriorityField
              label={'Priority'}
              value={loanTaskFormStore.form.fields.priority.value}
              onChange={(e, value) => loanTaskFormStore.onFieldChange('priority', value)}
              options={generalTaskPriorityOptions}
              disabled={false}
            />
          </Grid>
        )}
        <Grid item xs={12}>
          {createSelectField({
            options: !isInternalUser ? defaultSectionName : (options?.length > 0 ? options : defaultSectionName),
            label: 'Section',
            store: loanTaskFormStore,
            fieldName: 'sectionId',
            testId: 'sectionId',
            disabled: isSectionPreselected || !isInternalUser,
          })}
        </Grid>
        <Grid item xs={12}>
          {createTextField({
            store: loanTaskFormStore,
            testId: 'title',
            fieldName: 'title',
            label: 'Title',
            ref: inputRef,
          })}
        </Grid>
        <Grid item xs={12}>
          {createHtmlField({
            store: loanTaskFormStore,
            testId: 'description',
            fieldName: 'description',
            label: 'Description',
            multiline: true,
            minRows: 2,
          })}
        </Grid>
      </Grid>
    </>

  );
});
